import { useEffect, useState } from 'react';
import { message, Table, Button, Card, Tabs, Typography } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { dateFormat, tablePageSize } from '../../../utils/constants';
import { usePageTitle } from '../../../context/pageTitleContext';
import { useTranslation } from 'react-i18next';
import LocalfilesServices from '../../../services/localFilesServices';
import NewLocalFileLog from './newLocalFileLog';
import dayjs from 'dayjs';
import { Can, permissions, roles } from '../../../utils/permissionUtils';
import { useUserAuth } from '../../../context/UserContext';
import UsersServices from '../../../services/usersServices';
import { LocalFile, LocalFileLog, UserLocalFiles } from './localFiles.types';
import NewLocalFile from './newLocalFile';
import UpdateLocalFile from './updateLocalFile';
import './localFiles.scss';
const { Link } = Typography;

const LocalFiles = () => {
    const { t } = useTranslation();
    const { setPageTitle } = usePageTitle();
    const [localFilesLogs, setLocalFilesLogs] = useState<LocalFileLog[]>([]);
    const [loadingData, setLoadingData] = useState(false);
    const [openCreateDrawer, setOpenCreateDrawer] = useState(false);
    const [openCreateLocalFileDrawer, setOpenCreateLocalFileDrawer] = useState(false);
    const [openUpdateLocalFileDrawer, setOpenUpdateLocalFileDrawer] = useState(false);
    const [selectedLocalFile, setSelectedLocalFile] = useState<any>(null);
    const [localFilesList, setLocalFilesList] = useState<LocalFile[]>([]);
    const [activeTab, setActiveTab] = useState('1');
    const { user } = useUserAuth();
    const [loadingLocalFilesList, setLoadingLocalFilesList] = useState(false);
    const [userLocalFiles, setUserLocalFiles] = useState<UserLocalFiles[]>([]);

    useEffect(() => {
        setPageTitle(t('page.localFiles'));
        getLocalFilesLogs();
        getLocalFilesList();
        if (user) {
            getUserLocalFiles(user.id);
        }
    }, []);

    const getUserLocalFiles = async (userId: string) => {
        try {
            const respData = await UsersServices.getUserLocalFiles(userId);
            if (respData.success) {
                const res = respData.responseObject.value.userLocalFiles;
                setUserLocalFiles(res);
            }
            else {
            }
        } catch (error) {
        } finally {
        }
    };

    const getLocalFilesList = async () => {
        setLoadingLocalFilesList(true);
        try {
            const respData = await LocalfilesServices.getLocalFilesList();
            if (respData.success) {
                const res = respData.responseObject.value.localFiles;
                setLocalFilesList(res);
            }
            else {
                message.error(t('messages.errorAccured'));
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        } finally {
            setLoadingLocalFilesList(false);
        }
    };

    const showDrawer = (drawerName: string, selectedLocalFile: any) => {
        if (drawerName === "create") setOpenCreateDrawer(true);
        if (drawerName === "createLocalFile") setOpenCreateLocalFileDrawer(true);
        if (drawerName === "update") {
            setOpenUpdateLocalFileDrawer(true);
            setSelectedLocalFile(selectedLocalFile);
        }
    }

    const onCloseDrawer = (drawerName: string) => {
        if (drawerName === "create") {
            setOpenCreateDrawer(false);
            setOpenCreateLocalFileDrawer(false);
        }
        if (drawerName === "update") {
            setOpenUpdateLocalFileDrawer(false);
            setSelectedLocalFile(null);
        }
    };

    const getLocalFilesLogs = async () => {
        let userId = null
        if (user.roleCode != roles.admin && user.roleCode != roles.powerUser) {
            userId = user.id;
        }

        setLoadingData(true);
        try {
            const respData = await LocalfilesServices.getLocalFilesLogs(userId);
            if (respData.success) {
                const res = respData.responseObject.value.log;
                setLocalFilesLogs(res);
            }
            else {
                message.error(t('messages.errorAccured'));
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        } finally {
            setLoadingData(false);
        }
    };

    const columns = [
        {
            title: t('localFiles.localFileType'),
            dataIndex: 'localFileName',
            key: 'localFileName',
        },
        {
            title: t('localFiles.fileName'),
            dataIndex: 'fileName',
            key: 'fileName',
        },
        {
            title: t('localFiles.extension'),
            dataIndex: 'fileExtension',
            key: 'fileExtension',
        },
        {
            title: t('localFiles.uploadDate'),
            dataIndex: 'uploadDate',
            key: 'uploadDate',
            render: (text: string) => (text ? dayjs(text).format(dateFormat) : ""),
        },
        {
            title: t('localFiles.processedDate'),
            dataIndex: 'processedDate',
            key: 'processedDate',
            render: (text: string) => (text ? dayjs(text).format(dateFormat) : ""),
        },
        {
            title: t('users.username'),
            dataIndex: 'username',
            key: 'username',
        },
        // {
        //     key: "action",
        //     width: "50px",
        //     render: (_, record) => {
        //         return (
        //             <Space>
        //                 <Link onClick={() => showDrawer("update", record)}><EditOutlined className='editIcon' /></Link >
        //             </Space>
        //         )
        //     }
        // }

    ];

    const localFilesListColumns = [
        {
            title: t('localFiles.localFileName'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('localFiles.description'),
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: t('localFiles.extension'),
            dataIndex: 'fileExtension',
            key: 'fileExtension',
        },
        {
            title: t('localFiles.localFileTemplate'),
            dataIndex: 'templateFilePath',
            key: 'templateFilePath',
            render: (text: string) => (text ? <Link download href={text}>{t('localFiles.viewTemplate')}</Link> : ""),
        },
        // {
        //     title: '',
        //     dataIndex: 'action',
        //     key: 'action',
        //     width: '30px',
        //     render: (_: any, record: any) => {
        //         return (
        //             <Can permission={permissions.UPDATE_LOCAL_FILE}>
        //                 <EditOutlined onClick={() => showDrawer("update", record)} />
        //             </Can>
        //         )
        //     }
        // }
    ];

    const LocalFilesLogs = () => {
        return (
            <Card size='small' extra={
                <Can permission={permissions.CREATE_LOCAL_FILE}>
                    <Button size='small' type="default" onClick={() => showDrawer("create", null)} icon={<PlusOutlined />}>{t('localFiles.uploadLocalFile')}</Button>
                </Can>
            }>
                <Table
                    dataSource={localFilesLogs}
                    columns={columns}
                    rowKey={(obj: LocalFileLog) => obj.logID || ''}
                    pagination={{
                        pageSize: tablePageSize
                    }}
                    loading={loadingData}
                    size='small'
                    bordered
                />
            </Card>
        )
    }

    const LocalFilesList = () => {
        return (
            <Card size='small' 
            // extra={
            //     <Can permission={permissions.CREATE_LOCAL_FILE}>
            //         <Button size='small' type="default" onClick={() => showDrawer("createLocalFile", null)} icon={<PlusOutlined />}>{t('localFiles.newLocalFile')}</Button>
            //     </Can>
            // }
            >
                <Table
                    dataSource={localFilesList}
                    columns={localFilesListColumns}
                    rowKey={obj => obj.id}
                    size='small'
                    bordered
                    loading={loadingLocalFilesList}
                />
            </Card>
        )
    }

    const items = [
        {
            key: '1',
            label: t('localFiles.localFiles'),
            children: <LocalFilesList />,
        },
        {
            key: '2',
            label: t('localFiles.localFilesLogs'),
            children: <LocalFilesLogs />,
        }

    ]

    const onSetActiveTab = (key: string) => {
        setActiveTab(key);
    }

    return (
        <div className='localFilesPage'>
            {localFilesList.length > 0 && (
                <NewLocalFileLog open={openCreateDrawer} onClose={() => onCloseDrawer("create")} getLocalFilesLogs={() => getLocalFilesLogs()} setActiveTab={onSetActiveTab} localFilesList={localFilesList} userLocalFilesList={userLocalFiles} />
            )}

            {/* <NewLocalFile open={openCreateLocalFileDrawer} onClose={() => onCloseDrawer("create")} getLocalFilesList={() => getLocalFilesList()} /> */}

            {/* {selectedLocalFile && (
                <UpdateLocalFile open={openUpdateLocalFileDrawer} onClose={() => onCloseDrawer("update")} getLocalFilesList={() => getLocalFilesList()} selectedLocalFile={selectedLocalFile} />
            )} */}

            <Tabs defaultActiveKey={activeTab} activeKey={activeTab} items={items} onChange={setActiveTab} />

        </div>
    )
}

export default LocalFiles;